import { ComponentProps, RefObject } from 'preact';
import { ForwardedRef, forwardRef, useCallback } from 'preact/compat';

import { Floating } from '../UNSAFE_Floating';
import { Layer } from '../UNSAFE_Layer';

import { MenuContainer } from './MenuContainer';
import { BaseMenuProps } from './Menu';
import { keyElemExtractor } from './menuUtils';
import { useMenuContext } from './useMenuContext';
import { dropdownMenuStyles } from './themes/DropdownMenuStyles.css';

type DropdownMenuProps = BaseMenuProps & DropdownMenuFloatingProps;

type FloatingProps = ComponentProps<typeof Floating>;
type DropdownMenuFloatingProps = {
  defaultPlacement: FloatingProps['placement'];
  offsetValue: FloatingProps['offsetValue'];
  flipOptions: FloatingProps['flipOptions'];
};

//TODO: Start consuming PRIVATE_Dropdown. JET-53371
/*Component used to render a dropdown menu. For now this is just rendered when device is not a phone. */
export const DropdownMenu = forwardRef(
  (
    {
      anchorRef,
      defaultPlacement,
      offsetValue,
      closeSubmenu,
      children,
      'aria-label': ariaLabel,
      currentFocus,
      menuId,
      menuLevel,
      flipOptions
    }: DropdownMenuProps,
    ref?: ForwardedRef<HTMLDivElement>
  ) => {
    const { onClose } = useMenuContext();

    const useOutsideClickHandler = useCallback(
      (e: MouseEvent) => {
        if (menuLevel === 'sub') return;
        // Outside click is not considered when menuItem was clicked
        const menuItemKey = keyElemExtractor(e.target as HTMLElement)?.key;
        if (!menuItemKey) {
          onClose?.({ reason: 'outsideClick', target: e.target as Element });
        }
      },
      [onClose, menuLevel]
    );

    // TODO - Reuse a dropdown component instead of creating floating by ourselves.

    /* 
    For this comment, we could divide menus in these categories:
    - Dropdown menus: "Attached" to an specific element.
    - Context menus: "Attached" to coords or an specific element.
    We are passing anchorRef as the logicalParent of the Layer used here, even if we pass coords. So we could say 
    that we are passing an incorrect logicalParentRef for context menus. But, passing coords has no impacts either on 
    accessibility or behavior since this logical parent is only used by legacy collections for dom searching to find 
    dropdown menus. More information about that here:  
    This comment also applies for sheet menus that in this specific context(relation between logicalParentRef
    and menu) can be considered "dropdown menus"
    For now using type assertion is safe enough, but it's kind of a dirty solution. This is a point that can be included 
    in near future conversation related to semantics of menus in favor of dividing dropdown and context menu: 
      
    */

    return (
      <Layer logicalParentRef={anchorRef as RefObject<Element>}>
        <Floating
          class={dropdownMenuStyles.base}
          onClickOutside={useOutsideClickHandler}
          anchorRef={anchorRef}
          flipOptions={flipOptions}
          shiftOptions={{
            mainAxis: false,
            crossAxis: false
          }}
          sizeOptions={{ isMaxHeightAdjusted: true, isMaxWidthAdjusted: true }}
          //TODO: JET-57950. Need to see how we set 24px boundary.
          //TODO: Boundaries need to match with the dropdowns used in other comps.  Also figure out how to make it themable.
          boundaryOptions={{ padding: 0 }}
          placement={defaultPlacement}
          offsetValue={offsetValue}>
          <div
            //TODO: JET-60745. Add minWidth prop on menu
            style={{
              minWidth: MENU_MIN_WIDTH
            }}
            ref={ref}>
            <MenuContainer
              menuLevel={menuLevel}
              closeSubmenu={closeSubmenu}
              aria-label={ariaLabel}
              currentFocus={currentFocus}
              menuId={menuId}
              display="dropdown"
              anchorRef={anchorRef}>
              {children}
            </MenuContainer>
          </div>
        </Floating>
      </Layer>
    );
  }
);

const MENU_MIN_WIDTH = 200;
